/**
 * Selectable, expandable table of released delivery lines for the Delivery
 * Lines sub-tab. Each row has a checkbox for multi-select and a chevron
 * toggle that reveals a detail panel. Extracted from DeliveryLinesTab to keep
 * both files under the 500-line limit.
 *
 * Backed by `call_off.DeliveryLine` (the DNA ERP import). Monetary fields
 * (unit price, line value, currency, GBP-converted value) come from the linked
 * PO line and are surfaced both as a grid "Value" column — the line's
 * transaction-currency total with the GBP-converted value on a sub-line/tooltip
 * — and in the expanded detail panel. Fields the source row doesn't carry
 * render as '-'; they are NOT invented.
 */
import { Fragment } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DeliveryLine } from '../../../lib/api';

/** Platform base/group currency — converted values are stored in GBP. */
const BASE_CURRENCY = 'GBP';

interface DeliveryLinesTableProps {
  lines: DeliveryLine[];
  selectedIds: Set<number>;
  expandedId: number | null;
  onToggleSelect: (line: DeliveryLine) => void;
  onToggleSelectAll: () => void;
  onToggleExpand: (id: number) => void;
  formatDate: (dateString?: string | null) => string;
  /**
   * Format a monetary amount in a given currency. Sourced from
   * PreferencesContext's `formatCurrency` so the Value column + detail-panel
   * money fields follow the tenant's number/currency preferences.
   */
  formatCurrency: (value: number, currency?: string, showDecimals?: boolean | number) => string;
  /** When true, render `customer_name` in the counterparty column instead of `supplier_name`. */
  showCustomerAsSupplier: boolean;
  /** Column header for the counterparty/site column (Supplier, Site, or Customer). */
  counterpartyColumnLabel: string;
}

/** A label/value pair in the expanded detail panel. */
function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm font-medium">{value || '-'}</span>
    </div>
  );
}

/** Parse a nullable decimal string into a number, or null if absent/invalid. */
function num(value: string | null): number | null {
  if (value == null) return null;
  const n = parseFloat(value);
  return Number.isNaN(n) ? null : n;
}

/**
 * Line value in the line's transaction currency (unit_price × ordered qty),
 * or null when the linked PO line didn't resolve so the cell renders '-'.
 */
function transactionValue(line: DeliveryLine): number | null {
  const price = num(line.unit_price);
  const qty = num(line.ordered);
  if (price == null || qty == null) return null;
  return price * qty;
}

/**
 * Released delivery lines table with row selection and expandable detail
 * panels. All selection/expansion state is owned by the parent.
 */
export function DeliveryLinesTable({
  lines,
  selectedIds,
  expandedId,
  onToggleSelect,
  onToggleSelectAll,
  onToggleExpand,
  formatDate,
  formatCurrency,
  showCustomerAsSupplier,
  counterpartyColumnLabel,
}: DeliveryLinesTableProps) {
  const allSelected = lines.length > 0 && lines.every((l) => selectedIds.has(l.id));

  return (
    <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-350px)]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 z-20">
          <tr className="border-b bg-muted/80 backdrop-blur-sm">
            <th className="py-3 px-4 w-10">
              <input
                type="checkbox"
                aria-label="Select all delivery lines"
                checked={allSelected}
                onChange={onToggleSelectAll}
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
              />
            </th>
            <th className="py-3 px-4 w-10" />
            <th className="text-left py-3 px-4 font-medium min-w-[160px]">{counterpartyColumnLabel}</th>
            <th className="text-left py-3 px-4 font-medium min-w-[120px]">SO Number</th>
            <th className="text-left py-3 px-4 font-medium min-w-[70px]">Line</th>
            <th className="text-left py-3 px-4 font-medium min-w-[120px]">Date Released</th>
            <th className="text-right py-3 px-4 font-medium min-w-[110px]">Qty Released</th>
            <th className="text-right py-3 px-4 font-medium min-w-[150px]">Value</th>
            <th className="text-left py-3 px-4 font-medium min-w-[140px]">Delivery Number</th>
            <th className="text-left py-3 px-4 font-medium min-w-[200px]">Description</th>
            <th className="text-left py-3 px-4 font-medium min-w-[140px]">Part Number</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => {
            const isExpanded = expandedId === line.id;
            const txnValue = transactionValue(line);
            const gbpValue = num(line.converted_total_value);
            const valueTitle =
              gbpValue != null && line.currency && line.currency !== BASE_CURRENCY
                ? `${formatCurrency(gbpValue, BASE_CURRENCY, 2)} (converted to ${BASE_CURRENCY})`
                : undefined;
            return (
              <Fragment key={line.id}>
                <tr
                  className={`border-b hover:bg-muted/50 transition-colors ${selectedIds.has(line.id) ? 'bg-blue-50/50' : ''}`}
                >
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      aria-label={`Select line ${line.po_line_number ?? line.id}`}
                      checked={selectedIds.has(line.id)}
                      onChange={() => onToggleSelect(line)}
                      className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <button
                      type="button"
                      aria-label={isExpanded ? 'Collapse line detail' : 'Expand line detail'}
                      onClick={() => onToggleExpand(line.id)}
                      className="p-1 hover:bg-muted rounded transition-colors"
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                  </td>
                  <td className="py-3 px-4">{(showCustomerAsSupplier ? line.customer_name : line.supplier_name) || '-'}</td>
                  <td className="py-3 px-4 font-medium">{line.po_order_number || '-'}</td>
                  <td className="py-3 px-4">{line.po_line_number ?? '-'}</td>
                  <td className="py-3 px-4">{formatDate(line.date_released)}</td>
                  <td className="py-3 px-4 text-right">{line.qty_released || '-'}</td>
                  <td className="py-3 px-4 text-right" title={valueTitle}>
                    {txnValue != null ? (
                      <div className="flex flex-col items-end">
                        <span className="font-medium">
                          {formatCurrency(txnValue, line.currency || BASE_CURRENCY, 2)}
                        </span>
                        {gbpValue != null && line.currency && line.currency !== BASE_CURRENCY && (
                          <span className="text-xs text-muted-foreground">
                            {formatCurrency(gbpValue, BASE_CURRENCY, 2)}
                          </span>
                        )}
                      </div>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-3 px-4">{line.delivery_number || '-'}</td>
                  <td className="py-3 px-4">{line.part_description || '-'}</td>
                  <td className="py-3 px-4 font-mono">{line.part_number || '-'}</td>
                </tr>
                {isExpanded && (
                  <tr className="border-b bg-muted/20">
                    <td colSpan={11} className="p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        <DetailRow label="Description" value={line.part_description || ''} />
                        <DetailRow label="Source Type" value={line.source_type || ''} />
                        <DetailRow label="Ordered" value={line.ordered || ''} />
                        <DetailRow label="Qty Delivered" value={line.qty_delivered || ''} />
                        <DetailRow label="Currency" value={line.currency || ''} />
                        <DetailRow
                          label="Unit Price"
                          value={num(line.unit_price) != null ? formatCurrency(num(line.unit_price) as number, line.currency || BASE_CURRENCY, 2) : ''}
                        />
                        <DetailRow
                          label="Line Value"
                          value={txnValue != null ? formatCurrency(txnValue, line.currency || BASE_CURRENCY, 2) : ''}
                        />
                        <DetailRow
                          label={`Converted Unit Price (${BASE_CURRENCY})`}
                          value={num(line.converted_unit_price) != null ? formatCurrency(num(line.converted_unit_price) as number, BASE_CURRENCY, 2) : ''}
                        />
                        <DetailRow
                          label={`Converted Total Value (${BASE_CURRENCY})`}
                          value={gbpValue != null ? formatCurrency(gbpValue, BASE_CURRENCY, 2) : ''}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
